/// <reference types='google.maps' />
import { Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { GenericService } from 'libs/portal/src/services/generic/generic.service';
import { Endpoints } from '@huge-monorepo/portal/config';
import { ToolsService } from 'libs/portal/src/services/tools/tools.service';

interface LocationRadiusModalData {
  celltower: string;
  currentRingFenceValue?: number;
  deviceIds: number[];
  alertid: string;
  device: {
    imei: any;
    terminalid: string;
    location: {
      lat: number;
      lng: number;
    };
    data: any;
  };
  description: string;
  viewOnly?: boolean;
}

@Component({
  selector: 'app-location-radius-modal',
  templateUrl: './location-radius-modal.component.html',
  styleUrls: ['./location-radius-modal.component.scss'],
})
export class LocationRadiusModalComponent implements OnInit {
  @ViewChild('map', { static: false }) gmap: ElementRef;
  viewOnly: boolean;
  accepted = false;
  map: google.maps.Map;
  deviceIds: number[];
  celltower: number;
  alertid: number;
  rangeValue = {
    value: 0,
  };
  terminalid: string;
  lat: number;
  lng: number;
  imei: string;
  description: string;
  isBatch: boolean;
  data: any[];
  mapOptions: google.maps.MapOptions;
  markers: google.maps.Marker[] = [];
  circles: google.maps.Circle[] = [];
  previousLocationUnknown = false;
  myForm = new UntypedFormGroup({
    formRange: new UntypedFormControl(0, []),
  });

  constructor(
    private genericService: GenericService,
    private endpoints: Endpoints,
    private dialogRef: MatDialogRef<LocationRadiusModalComponent>,
    @Inject(MAT_DIALOG_DATA) private dialogData: LocationRadiusModalData,
    private tools: ToolsService,
  ) {}

  async closeModal(data: any) {
    this.dialogRef.close(data);
  }

  setRingFence() {
    if (this.deviceIds.length > 0) {
      const ringFenceObj = {
        DeviceID: this.isBatch ? this.deviceIds : this.deviceIds[0],
        Longitude: this.lng,
        Latitude: this.lat,
        Radius: this.rangeValue.value * 1000,
        TowerID: this.celltower,
      };

      let url = '';
      if (this.alertid != null && this.alertid > 0) {
        url = this.endpoints.ACCEPT_RINGFENCE_LOCATION();
        ringFenceObj['AlertID'] = this.alertid;
      } else {
        url = this.isBatch ? this.endpoints.ADD_BATCH_RING_FENCE_LOG : this.endpoints.ADD_RING_FENCE_LOG(this.deviceIds[0]);
      }

      this.genericService.POST(url, ringFenceObj, true).subscribe(() => {
        this.closeModal(ringFenceObj);
      });
    }
  }

  changeRadius(eve) {
    this.rangeValue = {
      value: eve.detail.value,
    };
    if (this.map != null) {
      if (this.circles != null && this.circles.length > 0) {
        this.circles[0].setRadius(this.rangeValue.value * 1000);
      }
    }
  }

  ngOnInit() {
    if (this.dialogData != null) {
      if (this.dialogData.deviceIds.length > 1) {
        this.isBatch = true;
      }
      this.deviceIds = this.dialogData.deviceIds;
      this.rangeValue = {
        value: this.dialogData.currentRingFenceValue != null ? this.dialogData.currentRingFenceValue : 0,
      };
      this.lat = this.dialogData.device.location.lat;
      this.lng = this.dialogData.device.location.lng;
      this.imei = this.dialogData.device.imei;
      this.description = this.dialogData.description;
      this.data = this.dialogData.device.data;
      this.terminalid = this.dialogData.device.terminalid;
      this.viewOnly = this.dialogData.viewOnly != null ? this.dialogData.viewOnly : false;
    }

    setTimeout(() => {
      this.initialiseMap(() => {
        this.drawMarkers([
          {
            label: this.terminalid,
            Lat: this.lat,
            Long: this.lng,
          },
        ]);
      });

      // Custom scroll zoom logic
      const mapContainer = document.getElementById('map'); // Ensure this ID is correct
      if (mapContainer) {
        mapContainer.addEventListener('wheel', (event: WheelEvent) => this.tools.handleZoom(event, this.map));
      }
    }, 1000);
  }

  drawMarkers(data: any[]): void {
    if (this.map == null) {
      return;
    }

    data.forEach((location: any) => {
      const center = new google.maps.LatLng(location.Lat, location.Long);
      this.map.setCenter(center);
      const marker = new google.maps.Marker({
        position: center,
        map: this.map,
        label: this.accepted ? 'Accepted' : 'Current',
      });
      const radius = new google.maps.Circle({
        strokeColor: this.accepted ? '#008000' : '#FF0000',
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: this.accepted ? '#008000' : '#FF0000',
        fillOpacity: 0.35,
        map: this.map,
        center: center,
        radius: this.rangeValue.value * 1000,
      });
      this.circles.push(radius);

      this.markers.push(marker);
    });

    if (this.data != null && this.data['PrevPoint'] != null) {
      const prevLat = this.data['PrevPoint'].Lat.toFixed(2);
      const prevLong = this.data['PrevPoint'].Lng.toFixed(2);
      if (prevLat != null && prevLat != 0.0 && !Number.isNaN(prevLat) && prevLong != null && prevLong != 0.0 && !Number.isNaN(prevLong)) {
        if (
          !(
            (prevLat == this.data['Point'].Lat.toFixed(2) && prevLong == this.data['Point'].Lng.toFixed(2)) ||
            (prevLat == this.data['Center'].Lat.toFixed(2) && prevLong == this.data['Center'].Lng.toFixed(2))
          )
        ) {
          const prevCenter = new google.maps.LatLng(this.data['PrevPoint'].Lat, this.data['PrevPoint'].Lng);

          this.circles.push(
            new google.maps.Circle({
              strokeColor: '#DA70D6',
              strokeOpacity: 0.8,
              strokeWeight: 2,
              fillColor: '#DA70D6',
              fillOpacity: 0.35,
              map: this.map,
              center: prevCenter,
              radius: this.data['Radius'],
            }),
          );

          this.markers.push(
            new google.maps.Marker({
              position: prevCenter,
              map: this.map,
              label: 'Previous',
            }),
          );
        }
        const acceptedCenter = new google.maps.LatLng(this.data['Center'].Lat, this.data['Center'].Lng);

        this.circles.push(
          new google.maps.Circle({
            strokeColor: '#008000',
            strokeOpacity: 0.8,
            strokeWeight: 2,
            fillColor: '#008000',
            fillOpacity: 0.35,
            map: this.map,
            center: acceptedCenter,
            radius: this.data['Radius'],
          }),
        );

        this.markers.push(
          new google.maps.Marker({
            position: acceptedCenter,
            map: this.map,
            label: 'Expected',
          }),
        );
      } else {
        this.previousLocationUnknown = true;
      }
    }

    const line = new google.maps.Polyline({
      path: this.markers.map((mark) => {
        return mark.getPosition();
      }),
      geodesic: true,
      strokeColor: '#000',
      strokeOpacity: 1.0,
      strokeWeight: 2,
    });

    line.setMap(this.map);
  }

  initialiseMap(callback: Function) {
    const currentCenter = new google.maps.LatLng(this.lat, this.lng);
    this.mapOptions = {
      center: currentCenter,
      zoom: 9,
      scaleControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      mapTypeControl: false,
      minZoom: 4,
      zoomControl: true,
    };
    if (this.gmap) {
      if (!this.map) {
        this.map = new google.maps.Map(this.gmap.nativeElement, this.mapOptions);
      } else {
        this.map.unbindAll();
      }
    }
    if (callback != null) {
      setTimeout(() => {
        callback();
      });
    }
  }
}
